import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { getRequestWithAuth, postRequestWithAuth } from '~/lib/api';
import { useAuth } from '~/contexts/AuthContext';
import { getWizardOAuthState, clearWizardOAuthState } from '~/lib/wizard-oauth-state';

type CallbackState = 'processing' | 'error';

export default function ShopifyCallback() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Connecting your store...');
  const [callbackState, setCallbackState] = useState<CallbackState>('processing');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshAuth } = useAuth();
  const processingRef = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double execution from React Strict Mode
      if (processingRef.current) return;
      processingRef.current = true;
      const code = searchParams.get('code');
      const shop = searchParams.get('shop');
      const hmac = searchParams.get('hmac');
      const host = searchParams.get('host');
      const state = searchParams.get('state');
      const timestamp = searchParams.get('timestamp');

      // CASE 1: No code - this is the post-install redirect from managed installation
      // Need to initiate OAuth authorization code flow to get the code
      if (!code && shop && hmac && timestamp) {
        setStatus('Authorizing with Shopify...');
        try {
          const { authorization_url } = await getRequestWithAuth(
            `/api/v1/shopify/authorize?shop=${encodeURIComponent(shop)}`
          );
          // Redirect to Shopify to get the authorization code
          window.location.href = authorization_url;
        } catch (err: any) {
          setError(err.response?.data?.detail || 'Failed to initiate authorization');
          setCallbackState('error');
        }
        return;
      }

      // CASE 2: Has code - this is the OAuth callback with authorization code
      if (!code || !shop || !hmac || !state || !timestamp) {
        setError('Missing required parameters');
        setCallbackState('error');
        return;
      }

      setStatus('Exchanging token...');
      try {
        const response = await postRequestWithAuth('/api/v1/shopify/callback', {
          code,
          shop,
          hmac,
          host,
          state,
          timestamp,
        });

        // Refresh user data to get updated setup_needed
        await refreshAuth();

        // Clear scopeStatus cache so re-auth flow doesn't use stale data
        queryClient.removeQueries({ queryKey: ['scopeStatus'] });

        // Check for wizard state - if user was in wizard, return there with connection info
        const wizardState = getWizardOAuthState();
        if (wizardState?.oauthType === 'shopify') {
          clearWizardOAuthState();
          const connectionId = response?.connectionId;

          // Validate connectionId - backend should always return it on success
          if (!connectionId) {
            setError('Connection succeeded but no connection ID was returned. Please try again.');
            setCallbackState('error');
            return;
          }

          navigate(
            `/app/dashboard?shopifyOAuthReturn=success&shopifyConnectionId=${connectionId}`,
            { replace: true }
          );
        } else {
          // Standard flow (direct connection without wizard)
          navigate('/app/dashboard?newConnection=true', { replace: true });
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to connect store');
        setCallbackState('error');
      }
    };

    processCallback();
  }, [searchParams, refreshAuth, navigate, queryClient]);

  // Error state
  if (callbackState === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <div className="card bg-base-200 w-96">
          <div className="card-body">
            <h2 className="card-title text-error">Connection Failed</h2>
            <p className="text-sm text-base-content/70">{error}</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate('/app/dashboard')}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="flex items-center justify-center min-h-screen bg-base-100">
      <div className="text-center">
        <span className="loading loading-spinner loading-lg" />
        <p className="mt-4 text-base-content/70">{status}</p>
      </div>
    </div>
  );
}
